// XRayMe.js
import React, { useEffect, useRef } from "react";

/**
 * X-Ray Me (React)
 * - Interactive on desktop (≥992px) so it matches your d-lg-* visibility.
 * - No global touch/mouse listeners; everything is scoped to the top SVG.
 * - No preventDefault, so mobile pages can scroll normally.
 */
export default function XRayMe({
  imgTop = "/images/b.svg",      // the “revealed” image
  imgBottom = "/images/a.svg",   // the base image
  radius = 0.08                  // 8% of shortest viewport side
}) {
  const wrapRef = useRef(null);
  const svgBottomRef = useRef(null);
  const svgTopRef = useRef(null);
  const circleRef = useRef(null);
  const ringRef = useRef(null);

  // Map pointer (client) coords to SVG coords using the TOP svg's CTM
  const cursorPoint = (e) => {
    const svg = svgTopRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    return ctm ? pt.matrixTransform(ctm.inverse()) : { x: 0, y: 0 };
  };

  useEffect(() => {
    // Only enable the interactive effect on desktop (aligns with Bootstrap lg)
    const mq = window.matchMedia("(min-width: 992px)");
    if (!mq.matches) return;

    const svgTop = svgTopRef.current;
    const circle = circleRef.current;
    const ring = ringRef.current;
    if (!svgTop || !circle || !ring) return;

    let rafId = null;
    let pending = null;

    const update = (coords) => {
      if (!coords) return;
      circle.setAttribute("cx", coords.x);
      circle.setAttribute("cy", coords.y);
      ring.setAttribute("cx", coords.x);
      ring.setAttribute("cy", coords.y);
    };

    const onMove = (e) => {
      pending = cursorPoint(e);
      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          update(pending);
          rafId = null;
        });
      }
    };

    const onDown = (e) => {
      // capture subsequent moves even if pointer leaves the element briefly
      try { svgTop.setPointerCapture(e.pointerId); } catch (_) {}
      onMove(e);
    };

    svgTop.addEventListener("pointermove", onMove, { passive: true });
    svgTop.addEventListener("pointerdown", onDown, { passive: true });

    // Center lens initially relative to TOP svg
    const bbox = svgTop.getBoundingClientRect();
    onMove({ clientX: bbox.left + bbox.width / 2, clientY: bbox.top + bbox.height / 2 });

    return () => {
      svgTop.removeEventListener("pointermove", onMove);
      svgTop.removeEventListener("pointerdown", onDown);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div className="xray-wrap" ref={wrapRef}>
      {/* Bottom layer */}
      <svg className="xray-svg" ref={svgBottomRef} xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#E7E7E8" />
        <image href={imgBottom} width="100%" height="100%" preserveAspectRatio="xMidYMid slice" />
      </svg>

      {/* Top layer with clip-path (the one we listen on) */}
      <svg className="xray-svg" ref={svgTopRef} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <clipPath id="xray-mask">
            <circle ref={circleRef} cx="50%" cy="50%" r={`${radius * 100}%`} />
          </clipPath>
        </defs>

        <g clipPath="url(#xray-mask)">
          <rect width="100%" height="100%" fill="#272730" />
          <image href={imgTop} width="100%" height="100%" preserveAspectRatio="xMidYMid slice" />
        </g>

        {/* Visible ring for the lens */}
        <circle
          ref={ringRef}
          cx="50%"
          cy="50%"
          r={`${radius * 100}%`}
          fill="transparent"
          vectorEffect="non-scaling-stroke"
          aria-hidden="true"
        />
      </svg>

      <style>{`
        .xray-wrap {
          position: relative;
          width: 100%;
          height: 350px;
          overflow: hidden;
          /* Allow page scroll on mobile even when tapping hero area */
          touch-action: pan-y;
          -webkit-overflow-scrolling: touch;
        }

        .xray-svg {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 140rem;              /* preserves your large artwork scale */
          transform: translate(-50%, -50%);
          pointer-events: auto;         /* ensure we receive pointer events */
        }

        @media (min-width: 768px) {
          .xray-wrap { height: 600px; }
        }

        @media (min-width: 1200px) {
          .xray-wrap { height: 750px; }
        }
      `}</style>
    </div>
  );
}